import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Box,
  InputAdornment,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { getUsersByRole } from '../../services/userService';
import { assignAssociate } from '../../services/assignmentService';
import { ROLE_COLORS } from '../../config/constants';

const AssignAssociateDialog = ({ open, onClose, studentId, onAssigned }) => {
  const [associates, setAssociates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [notes, setNotes] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      fetchAssociates();
      setSelectedId(null);
      setNotes('');
      setSearch('');
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchAssociates = async () => {
    setLoading(true);
    try {
      const response = await getUsersByRole('associate');
      setAssociates(response.data?.data?.users || []);
    } catch (err) {
      setError('Failed to load associates.');
      console.error('Error fetching associates:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredAssociates = associates.filter((a) =>
    (a.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (a.email || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleAssign = async () => {
    if (!selectedId) return;
    setSubmitting(true);
    setError(null);
    try {
      await assignAssociate(studentId, selectedId, notes);
      if (onAssigned) onAssigned();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign associate.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="assign-associate-dialog-title"
    >
      <DialogTitle
        id="assign-associate-dialog-title"
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        Assign Associate
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          size="small"
          placeholder="Search associates..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredAssociates.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 3 }}>
            No associates found.
          </Typography>
        ) : (
          <List
            dense
            sx={{
              maxHeight: 300,
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            {filteredAssociates.map((associate) => (
              <ListItem
                key={associate._id}
                onClick={() => setSelectedId(associate._id)}
                selected={selectedId === associate._id}
                sx={{
                  cursor: 'pointer',
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    '&:hover': { backgroundColor: 'primary.light' },
                  },
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      bgcolor: ROLE_COLORS.associate,
                      width: 36,
                      height: 36,
                      fontSize: '0.875rem',
                    }}
                  >
                    {associate.name?.charAt(0)?.toUpperCase() || 'A'}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={associate.name}
                  secondary={associate.email}
                />
                {selectedId === associate._id && (
                  <CheckIcon color="primary" fontSize="small" />
                )}
              </ListItem>
            ))}
          </List>
        )}

        <TextField
          fullWidth
          multiline
          rows={2}
          label="Notes (optional)"
          placeholder="Reason for assignment..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ mt: 2 }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={!selectedId || submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {submitting ? 'Assigning...' : 'Assign Associate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignAssociateDialog;
